/**
 * @flow
 * Unified form state cleanup module
 */
/**
 * Checks if a key matches the criteria for cleanup
 * @param {string} key - The key to check
 * @param {Object} criteria - The criteria to match against
 * @returns {boolean} - Whether the key matches
 */
export const doesKeyMatch = (key: string, criteria: Object): boolean => {
  const { step, fieldName, sectionName } = criteria;

  // Extract the section name from the key (e.g., "trusts" from "trusts.a87bb4ea-6f2d-4d2e-8c93-b7168899b848")
  const keySectionName = key.split('.')[0];

  // If no criteria provided, no match
  if (!step && !fieldName && !sectionName) {
    return false;
  }

  // Check if step criteria matches
  const stepMatches =
    step &&
    (key === step.sectionName ||
      key === step.id ||
      keySectionName === step.sectionName);

  // Check if fieldName criteria matches
  const fieldNameMatches =
    fieldName &&
    (key === fieldName || (key.includes('.') && key.endsWith(`.${fieldName}`)));

  // Check if sectionName criteria matches
  const sectionNameMatches = sectionName && keySectionName === sectionName;

  // Return true if any criteria matches, false otherwise
  return stepMatches || fieldNameMatches || sectionNameMatches || false;
};

/**
 * Checks if a value exists and is not undefined or null
 * @param {Object} value - The value to check
 * @param {string} fieldName - The field name to check
 * @returns {boolean} - Whether the value exists
 */
export const doesValueExist = (value: Object, fieldName: string): boolean => {
  return value?.[fieldName] !== undefined && value?.[fieldName] !== null;
};

/**
 * Determines if a field should be deleted based on various criteria
 * @param {Object} options - Options for the decision
 * @returns {boolean} - Whether the field should be deleted
 */
export const shouldDeleteField = ({
  matchesKey,
  valueExists,
  isMounted,
  existsInFieldsArray,
}: Object): boolean => {
  // For unrequired forms cleanup
  if (matchesKey !== undefined && valueExists !== undefined) {
    return matchesKey && valueExists;
  }

  // A field should be kept if:
  // 1. It's mounted, OR
  // 2. It's an amount field and any related amount field is mounted
  const shouldKeep = isMounted;

  // For unmounted fields cleanup
  // If existsInFieldsArray is undefined, default to false
  return !shouldKeep && existsInFieldsArray === true;
};

/**
 * Cleans up form values for a specific step
 * @param {Object} step - The step object
 * @param {Object} fieldNamesToCleanup - The field names to cleanup
 * @param {Object} currentSection - The current section
 * @param {Function} debugLog - The debug logging function
 * @returns {Object} - The updated section
 */
export const cleanupFormValuesForStep = (
  step: Object,
  fieldNamesToCleanup: Object,
  currentSection: Object,
  logger,
): Object => {
  if (!fieldNamesToCleanup) {
    logger(`No field names to cleanup for step "${step.name}"`);
    return currentSection;
  }

  if (!currentSection?.formValues) {
    logger(`No form values found for section "${step.sectionName}"`);
    return currentSection;
  }

  const valuesExist = Object.keys(currentSection.formValues).some((key) => {
    // Extract the section name from the key (e.g., "trusts" from "trusts.a87bb4ea-6f2d-4d2e-8c93-b7168899b848")
    const keySectionName = key.split('.')[0];
    return keySectionName === step.sectionName || key.includes(step.id);
  });

  if (!valuesExist) {
    return currentSection;
  }

  logger(`Values exist for step "${step.name}": ${valuesExist}`);

  // Create a copy of the current section to avoid mutating the original
  const updatedSection = {
    ...currentSection,
    formValues: { ...currentSection.formValues },
  };

  // Process each field name to cleanup
  Object.values(fieldNamesToCleanup).forEach((fieldNameToCleanup) => {
    logger(`Checking field "${fieldNameToCleanup}" for cleanup`);

    // Check each form value
    Object.entries(updatedSection.formValues).forEach(([key, value]) => {
      // Check if the key matches the criteria
      const matchesKey = doesKeyMatch(key, {
        step,
        fieldName: fieldNameToCleanup,
      });

      // Check if the value exists
      const valueExists = doesValueExist(value, fieldNameToCleanup);

      // Determine if the field should be deleted
      const shouldDelete = shouldDeleteField({
        matchesKey,
        valueExists,
      });

      if (shouldDelete) {
        logger(
          `DELETING key "${key}" because it matches field "${fieldNameToCleanup}"`,
          null,
          'error',
        );
        delete updatedSection.formValues[key];
      }
    });
  });

  return updatedSection;
};

/**
 * Cleans up unmounted fields from form values
 * @param {Object} options - Options for the cleanup
 * @returns {Object} - The cleaned form values
 */
export const cleanupUnmountedFields = ({
  fieldsArray,
  values,
  mountedFieldNames,
  logger,
  newFormState,
}: Object): Object => {
  logger('cleanupUnmountedFields called with:', {
    fieldsArrayLength: fieldsArray.length,
    valuesKeys: Object.keys(values),
  });

  // Helper function to check if a field is mounted in any section
  const isFieldMounted = (fieldName, objectKey = null) => {
    // Check if the field is mounted in the specific object key (section.id format)
    if (objectKey && mountedFieldNames[objectKey]?.includes(fieldName)) {
      return true;
    }

    // Check if the field is mounted in any section
    return Object.values(mountedFieldNames).some((fields) =>
      fields.includes(fieldName),
    );
  };

  return Object.entries(values).reduce(
    (acc, [key, value]) => {
      if (key.includes('.')) {
        const [sectionName, itemId] = key.split('.');

        // Check if the item exists in the form state
        // check multiple ways it may be stored
        const itemExists = Boolean(
          newFormState?.hasOwnProperty(itemId) ||
            newFormState?.[sectionName]?.find((item) => {
              return item.id === itemId;
            }) ||
            Object.keys(newFormState?.[itemId]?.[sectionName] ?? {}).length ||
            Object.keys(newFormState?.[itemId] ?? {}).length,
        );

        if (itemExists) {
          // Process each field in the item
          Object.keys(value).forEach((fieldName) => {
            // Check if the field is mounted in any section
            const isMounted = isFieldMounted(fieldName, key);

            // Check if the field exists in the fields array
            const existsInFieldsArray = fieldsArray.some(({ name }) =>
              name?.includes(fieldName),
            );

            // Determine if the field should be deleted
            const shouldDelete = shouldDeleteField({
              isMounted,
              existsInFieldsArray,
            });

            const isCurrentItem = Boolean(mountedFieldNames[key]);

            if (shouldDelete && isCurrentItem) {
              logger(
                `DELETE NESTED FIELD ${key} > ${fieldName}`,
                {
                  fieldValue: acc[key][fieldName],
                  remainingFields: Object.keys(acc[key]).filter(
                    (f) => f !== fieldName,
                  ),
                  mountedFieldNames,
                },
                'error',
              );

              delete acc[key][fieldName];
            }
          });
        } else {
          // If the item doesn't exist, delete the entire object
          logger(
            `DELETE ${key} because it doesn't exist in the form state`,
            {
              newFormState,
              mountedFieldNames,
            },
            'error',
          );
          delete acc[key];
        }
      } else {
        // Handle non-nested objects
        Object.keys(value).forEach((fieldName) => {
          // Check if the field is mounted in any section
          const isMounted = isFieldMounted(fieldName);

          // Check if the field exists in the fields array
          const existsInFieldsArray = fieldsArray.some(({ name }) =>
            name?.includes(fieldName),
          );

          // Determine if the field should be deleted
          const shouldDelete = shouldDeleteField({
            isMounted,
            existsInFieldsArray,
          });

          if (shouldDelete) {
            logger('DELETE NON-NESTED FIELD', key, fieldName, {
              fieldValue: acc[key][fieldName],
              remainingFields: Object.keys(acc[key]).filter(
                (f) => f !== fieldName,
              ),
            });

            delete acc[key][fieldName];
          }
        });
      }

      return acc;
    },
    { ...values },
  );
};

/**
 * Main function to clean up form values
 * @param {Object} options - Options for the cleanup
 * @returns {Object} - The cleaned form values
 */
export const cleanupFormValues = ({
  values,
  stepsToCleanup,
  fieldNamesMap,
  fieldsArray,
  mountedFieldNames,
  newFormState,
  logger,
}: Object): Object => {
  // First, clean up values for steps that are no longer required
  let cleanedValues = { ...values };

  if (stepsToCleanup?.length) {
    stepsToCleanup.forEach((step) => {
      const fieldNamesToCleanup = fieldNamesMap?.[step.name];

      if (fieldNamesToCleanup) {
        const currentSection = { formValues: cleanedValues };

        const updatedSection = cleanupFormValuesForStep(
          step,
          fieldNamesToCleanup,
          currentSection,
          logger,
        );

        cleanedValues = updatedSection.formValues;
      }
    });
  }

  // Then, clean up unmounted fields
  if (fieldsArray) {
    cleanedValues = cleanupUnmountedFields({
      fieldsArray,
      values: cleanedValues,
      mountedFieldNames: mountedFieldNames || {},
      newFormState,
      logger,
    });
  }

  return cleanedValues;
};
